'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { BrandLogo } from '@/components/layouts/brand-logo';
import { NAV_LINKS, type NavLink } from '@/components/common/nav-content';
import { cn } from '@/utils/cn';
import { useSuburbsList } from '@/features/suburb/api/suburbs.queries';
import { slugToSuburbName } from '@/features/suburb/utils/suburb-slug';

// Match logic:
//   href === '/'        → exact match only
//   href starts with /  → startsWith match
// `/suburbs` parent active when pathname === '/suburbs' OR any child route.
function isActive(
  pathname: string,
  href: string,
  children?: ReadonlyArray<{ href: string }>,
): boolean {
  if (pathname === href) return true;
  if (children?.some((child) => pathname === child.href || pathname.startsWith(child.href + '/'))) {
    return true;
  }
  if (href !== '/' && pathname.startsWith(href + '/')) return true;
  return false;
}

interface NavItemProps {
  link: NavLink;
  active: boolean;
  onNavigate?: () => void;
  onUpdate?: () => void;
}

function NavItem({ link, active, onNavigate, onUpdate }: NavItemProps) {
  if (!link.children?.length) {
    if (link.disabled) {
      return (
        <span
          title="No suburbs available currently"
          className="inline-flex min-[1361px]:text-base text-sm px-2 py-2.5 capitalize text-[#6b7785] cursor-not-allowed"
        >
          {link.label}
        </span>
      );
    }

    return (
      <Link
        href={link.href}
        prefetch={false}
        onClick={onUpdate}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'inline-flex min-[1361px]:text-base text-sm px-2 py-2.5 capitalize transition hover:text-[var(--user-green)]',
          active
            ? 'border-b-2 border-[var(--user-green)] font-medium text-[var(--primary-black)]'
            : 'text-[var(--secondary-grey)]',
        )}
      >
        {link.label}
      </Link>
    );
  }

  return (
    <SuburbsDropdown link={link} active={active} onNavigate={onNavigate} onUpdate={onUpdate} />
  );
}

function SuburbsDropdown({ link, active, onNavigate, onUpdate }: NavItemProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(event: MouseEvent) {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div ref={ref} className="inline-flex relative cursor-pointer">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-current={active ? 'page' : undefined}
        className={cn(
          'inline-flex items-center gap-1 px-2 py-2.5 min-[1361px]:text-base text-sm capitalize transition hover:text-[var(--user-green)] cursor-pointer',
          active
            ? 'border-b-2 border-[var(--user-green)] font-medium text-[var(--primary-black)]'
            : 'text-[var(--secondary-grey)]',
        )}
      >
        {link.label}
        <svg
          aria-hidden
          viewBox="0 0 12 8"
          className={cn('h-2 w-3 transition-transform', open && 'rotate-180')}
        >
          <path d="M1 1l5 5 5-5" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
      </button>
      {open && link.children?.length ? (
        <div
          role="menu"
          className="absolute top-full right-0 z-50 mt-2 w-56 rounded-md border border-[var(--user-border)] bg-white py-2 shadow-lg"
        >
          {link.children.map((child) => (
            <Link
              key={child.href}
              href={child.href}
              prefetch={false}
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onNavigate?.();
                onUpdate?.();
              }}
              className="block px-4 py-2 text-sm text-[var(--primary-black)] transition hover:bg-[var(--user-surface-muted)] hover:text-[var(--user-green)]"
            >
              {child.label}
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function SiteHeader({ setUpdate }: { setUpdate: () => void }) {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [suburbsOpen, setSuburbsOpen] = useState(false);

  const { data: suburbsData } = useSuburbsList({
    isActive: true,
    limit: 100,
    sortBy: 'displayOrder',
    sortOrder: 'ASC',
  });

  const dynamicNavLinks = NAV_LINKS.map((link) => {
    if (link.label === 'Suburbs') {
      const suburbChildren = suburbsData?.items?.length
        ? suburbsData.items.map((suburb) => ({
            label: slugToSuburbName(suburb.slug || suburb.name),
            href: `/suburbs/${suburb.slug}`,
          }))
        : [];

      return {
        ...link,
        children: suburbChildren.length ? suburbChildren : undefined,
        disabled: suburbChildren.length === 0,
      };
    }
    return link;
  });

  useEffect(() => {
    setUpdate();
  }, [pathname, setUpdate]);

  return (
    <header className="sticky top-0 z-1001 border-b border-[var(--user-border)] bg-white/95 backdrop-blur">
      <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-6 px-4 py-4 sm:px-6 lg:px-10">
        <Link href="/" prefetch={false} aria-label="DC Commercial home" className="shrink-0">
          <BrandLogo />
        </Link>

        <nav className="hidden items-center xl:flex" aria-label="Primary">
          {dynamicNavLinks.map((link) => (
            <NavItem
              key={link.href}
              link={link}
              active={isActive(pathname, link.href, link.children)}
              onUpdate={setUpdate}
            />
          ))}
        </nav>

        <button
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded border border-[var(--user-border)] text-[var(--primary-black)] xl:hidden"
          aria-expanded={mobileOpen}
          aria-controls="mobile-nav"
          aria-label="Toggle navigation"
          onClick={() => setMobileOpen((value) => !value)}
        >
          <span className="sr-only">Menu</span>
          <span aria-hidden className="text-lg">
            {mobileOpen ? '✕' : '☰'}
          </span>
        </button>
      </div>

      <div
        id="mobile-nav"
        className={cn(
          'border-t border-[var(--user-border)] bg-white xl:hidden',
          mobileOpen ? 'block' : 'hidden',
        )}
      >
        <nav className="mx-auto flex max-w-7xl flex-col px-4 py-3" aria-label="Mobile">
          {dynamicNavLinks.map((link) => {
            const active = isActive(pathname, link.href, link.children);
            if (!link.children?.length) {
              if (link.disabled) {
                return (
                  <span
                    key={link.href}
                    title="No suburbs available currently"
                    className="block rounded px-2 py-2.5 text-sm font-medium tracking-[0.12em] uppercase text-[#6b7785] cursor-not-allowed"
                  >
                    {link.label}
                  </span>
                );
              }

              return (
                <Link
                  key={link.href}
                  href={link.href}
                  prefetch={false}
                  onClick={() => setMobileOpen(false)}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'block rounded px-2 py-2.5 text-sm font-medium tracking-[0.12em] capitalize hover:bg-[var(--user-surface-muted)]',
                    active
                      ? 'bg-[var(--user-surface-muted)] text-[var(--user-green)]'
                      : 'text-[var(--primary-black)]',
                  )}
                >
                  {link.label}
                </Link>
              );
            }
            return (
              <div key={link.href} className="py-1">
                <button
                  type="button"
                  onClick={() => setSuburbsOpen((value) => !value)}
                  aria-expanded={suburbsOpen}
                  className={cn(
                    'flex w-full items-center justify-between rounded px-2 py-2.5 text-sm font-medium tracking-[0.12em] capitalize hover:bg-[var(--user-surface-muted)]',
                    active
                      ? 'bg-[var(--user-surface-muted)] text-[var(--user-green)]'
                      : 'text-[var(--primary-black)]',
                  )}
                >
                  {link.label}
                  <span aria-hidden className="text-base">
                    {suburbsOpen ? '-' : '+'}
                  </span>
                </button>
                {suburbsOpen ? (
                  <div className="ml-3 flex flex-col border-l border-[var(--user-border)] pl-3">
                    {link.children.map((child) => {
                      const childActive = pathname === child.href;
                      return (
                        <Link
                          key={child.href}
                          href={child.href}
                          prefetch={false}
                          onClick={() => setMobileOpen(false)}
                          aria-current={childActive ? 'page' : undefined}
                          className={cn(
                            'rounded px-2 py-2 text-sm hover:bg-[var(--user-surface-muted)] hover:text-[var(--primary-black)]',
                            childActive
                              ? 'font-semibold text-[var(--user-green)]'
                              : 'text-[var(--primary-black-muted)]',
                          )}
                        >
                          {child.label}
                        </Link>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </nav>
      </div>
    </header>
  );
}
